// src/pages/application/LeadsPage.js
// COMPACT VERSION - Matches MarketingToolsPage.js structure exactly
// White canvas with tabs inside, compact spacing

import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import CustomersPage from '../../components/leads/b2c/CustomersPage';
import CustomerDashboard from '../../components/leads/b2c/CustomerDashboard';
import { businessAPI } from '../../services/businessAPI';
import { customerAPI } from '../../services/customerAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function LeadsPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [businessData, setBusinessData] = useState(null);
  const [crmMode, setCrmMode] = useState('b2c');
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [subscriptionTier, setSubscriptionTier] = useState('diy'); // Track user's subscription tier

  useEffect(() => {
    loadCRMData();
    loadCustomers(); // FIX: Load customers immediately for dashboard
  }, []);

  // FIX: Add loadCustomers function to fetch customer data
  const loadCustomers = async () => {
    try {
      console.log('📊 LeadsPage: Loading customers for dashboard...');
      const response = await customerAPI.getAll();
      if (response.success) {
        setCustomers(response.data || []);
        console.log('✅ LeadsPage: Loaded', response.data?.length || 0, 'customers');
      }
    } catch (error) {
      console.error('❌ LeadsPage: Error loading customers:', error);
      setCustomers([]);
    }
  };

  const loadCRMData = async () => {
    try {
      setLoading(true);
      console.log('📊 LeadsPage: Loading CRM data...');

      // Load subscription tier from API
      try {
        const subscriptionResponse = await businessAPI.getSubscription();
        if (subscriptionResponse.success) {
          setSubscriptionTier(subscriptionResponse.data.currentTier);
          console.log('✅ LeadsPage: Subscription tier loaded:', subscriptionResponse.data.currentTier);
        }
      } catch (subError) {
        console.error('❌ Error loading subscription, defaulting to DIY:', subError);
        setSubscriptionTier('diy');
      }

      // Load business profile (optional - don't block if it fails)
      try {
        const profileResponse = await businessAPI.getProfile();
        if (profileResponse.success) {
          setBusinessData(profileResponse.profile);

          // Auto-detect B2C or B2B based on business type
          const businessType = profileResponse.profile?.businessType?.toLowerCase() || '';
          if (businessType.includes('consultant') || businessType.includes('agency') ||
              businessType.includes('b2b') || businessType.includes('corporate') ||
              businessType.includes('freelance') || businessType.includes('coaching')) {
            setCrmMode('b2b');
          } else {
            setCrmMode('b2c');
          }

          console.log('✅ LeadsPage: Business data loaded, mode:',
            businessType.includes('consultant') ? 'b2b' : 'b2c');
        }
      } catch (profileError) {
        console.log('⚠️ Business profile not found (this is OK), using defaults');
        // Don't throw - profile is optional
      }

    } catch (error) {
      console.error('❌ LeadsPage: Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (newMode) => {
    setCrmMode(newMode);
    console.log('CRM Mode changed to:', newMode);
    
    try {
      await businessAPI.updateCRMMode(newMode);
      console.log('✅ CRM mode preference saved');
    } catch (error) {
      console.error('❌ Failed to save CRM mode preference:', error);
    }
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: 'fas fa-chart-line' },
    { id: 'manage', name: crmMode === 'b2c' ? 'Customers' : 'Deals', icon: crmMode === 'b2c' ? 'fas fa-users' : 'fas fa-briefcase' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        if (crmMode === 'b2c') {
          return <CustomerDashboard customers={customers} />;
        } else {
          return (
            <div className="text-center py-24">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-briefcase text-blue-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Lead Pipeline Dashboard</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm">
                Pipeline analytics, deal tracking, and conversion insights coming soon.
              </p>
              <button 
                onClick={() => setActiveTab('manage')}
                className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i className="fas fa-plus mr-2"></i>
                Add Deal
              </button>
            </div>
          );
        }

      case 'manage':
        if (crmMode === 'b2c') {
          return (
            <CustomersPage
              businessData={businessData}
              customers={customers}
              setCustomers={setCustomers}
              onCustomersChange={loadCustomers} // FIX: Refresh dashboard when customers change
            />
          );
        } else {
          return (
            <div className="text-center py-24">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-hammer text-yellow-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Lead Pipeline</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm">
                Pipeline board, proposal generator, and AI tools coming soon.
              </p>
              <div className="inline-flex items-center space-x-2 bg-yellow-50 text-yellow-800 px-4 py-2 rounded-lg border border-yellow-200">
                <i className="fas fa-code text-sm"></i>
                <span className="text-sm font-medium">B2B Component In Development</span>
              </div>
            </div>
          );
        }

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner size="lg" text="Loading your CRM..." />
        </div>
      </DashboardLayout>
    );
  }

  // CRM Access Gate for DIY users
  if (subscriptionTier === 'diy') {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Customers & Leads</h1>
              <p className="text-sm text-gray-600 mt-1">CRM access requires Assisted Growth or higher</p>
            </div>

            {/* Upgrade Prompt Card */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-8 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-lock text-blue-600 text-3xl"></i>
              </div>

              <h2 className="text-xl font-bold text-gray-900 mb-2">CRM Access Not Included</h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Your current plan (DIY Starter) includes Marketing Studio only. Upgrade to Assisted Growth to unlock full CRM access for up to 500 customers.
              </p>

              {/* Feature Comparison */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left max-w-md mx-auto">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Unlock with Assisted Growth (£129/mo):</h3>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2 text-sm">
                    <i className="fas fa-check-circle text-green-600 mt-0.5"></i>
                    <span className="text-gray-700">Full CRM Access (up to 500 customers)</span>
                  </li>
                  <li className="flex items-start space-x-2 text-sm">
                    <i className="fas fa-check-circle text-green-600 mt-0.5"></i>
                    <span className="text-gray-700">8 AI auto-posts/month</span>
                  </li>
                  <li className="flex items-start space-x-2 text-sm">
                    <i className="fas fa-check-circle text-green-600 mt-0.5"></i>
                    <span className="text-gray-700">Monthly strategy call (30 mins)</span>
                  </li>
                  <li className="flex items-start space-x-2 text-sm">
                    <i className="fas fa-check-circle text-green-600 mt-0.5"></i>
                    <span className="text-gray-700">Email support (48hr)</span>
                  </li>
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/settings?tab=billing"
                  className="px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg inline-flex items-center"
                >
                  <i className="fas fa-arrow-up mr-2"></i>
                  Upgrade to Assisted Growth
                </a>
                <a
                  href="/marketing"
                  className="px-6 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Back to Marketing Studio
                </a>
              </div>

              {/* Help Text */}
              <p className="text-xs text-gray-500 mt-6">
                <i className="fas fa-info-circle mr-1"></i>
                Questions? Email us at support@digitaltrent.com
              </p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">

        {/* COMPACT Header - Matches MarketingToolsPage */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customers & Leads</h1>
            <p className="text-sm text-gray-600 mt-1">AI-powered customer relationship management</p>
          </div>

          {/* B2C/B2B Mode Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleModeChange('b2c')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                crmMode === 'b2c'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-users mr-2"></i>
              B2C
            </button>
            <button
              onClick={() => handleModeChange('b2b')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                crmMode === 'b2b'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-briefcase mr-2"></i>
              B2B
            </button>
          </div>
        </div>

        {/* WHITE CANVAS WITH TABS INSIDE - EXACT match to Marketing page */}
        <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
          
          {/* Tab Navigation - INSIDE the white canvas */}
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all cursor-pointer
                    ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <i className={`${tab.icon}`}></i>
                  <span>{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content - INSIDE the same white canvas */}
          <div className="min-h-[600px]">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}